import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, get, post, setCsrfToken } from './api.js';
import { setStoreTimezone } from './format.js';

export interface Branding {
  name: string;
  slug: string | null;
  isDemo: boolean;
  brand: Record<string, string>;
}

export interface Me {
  userId: string;
  name: string;
  companyId: string;
  permissions: string[];
  /** Papel e onde este usuário abre o painel. Vem do servidor. */
  papel?: string;
  rotuloDoPapel?: string;
  telaInicial?: string;
  csrfToken: string;
}

export interface StoreSettings {
  companyId: string;
  name: string;
  timezone: string;
  isDemo: boolean;
  brand: Record<string, string>;
  storeInfo: {
    address?: string;
    city?: string;
    state?: string;
    phone?: string;
    instagram?: string;
    openingHours?: string;
    notes?: string;
    unconfirmedFields: string[];
  };
  storeInfoConfirmed: boolean;
  sendWindow: { weekdays: number[]; startMinute: number; endMinute: number; closedDates: string[] };
  businessHours: { weekdays: number[]; startMinute: number; endMinute: number; closedDates: string[] };
  slotGranularityMinutes: number;
  bookingHoldMinutes: number;
  aiMonthlyBudgetCents: number;
  settings: {
    discountsEnabled: boolean;
    frequencyCapDays: number;
    quoteValidityHours: number;
    attributionWindowDays: number;
    isDemo: boolean;
  };
}

interface SessionValue {
  me: Me | null;
  branding: Branding | null;
  store: StoreSettings | null;
  loading: boolean;
  can: (permission: string) => boolean;
  login: (companySlug: string, email: string, password: string, lembrar?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  reloadStore: () => Promise<void>;
}

const Context = createContext<SessionValue | null>(null);

/** Aplica a paleta ativa da loja nas variáveis CSS (seção 3). */
function applyBrand(brand: Record<string, string> | undefined): void {
  if (!brand) return;
  const map: Record<string, string> = {
    primary: '--gl-primary',
    primaryBorder: '--gl-primary-border',
    primaryStrong: '--gl-primary-strong',
    ink: '--gl-ink',
    muted: '--gl-muted',
    background: '--gl-background',
    surface: '--gl-surface',
    border: '--gl-border',
    inputBorder: '--gl-input-border',
    accent: '--gl-accent',
    accentBorder: '--gl-accent-border',
    accentStrong: '--gl-accent-strong',
    onPrimary: '--gl-on-primary',
    onAccent: '--gl-on-accent',
    danger: '--gl-danger',
    onDanger: '--gl-on-danger',
    success: '--gl-success',
  };
  for (const [token, variable] of Object.entries(map)) {
    const value = brand[token];
    if (typeof value === 'string' && value.length > 0) {
      document.documentElement.style.setProperty(variable, value);
    }
  }
  if (typeof brand.fontFamily === 'string' && brand.fontFamily.length > 0) {
    document.documentElement.style.setProperty('--gl-font', brand.fontFamily);
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [branding, setBranding] = useState<Branding | null>(null);
  const [store, setStore] = useState<StoreSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const loadStore = useCallback(async () => {
    try {
      const response = await get<{ settings: StoreSettings }>('/api/settings');
      setStore(response.settings);
      setStoreTimezone(response.settings.timezone);
      applyBrand(response.settings.brand);
    } catch {
      // Sem permissão de leitura de configurações, o painel segue com a marca pública.
    }
  }, []);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const publicBranding = await get<Branding>('/api/public/branding');
      setBranding(publicBranding);
      applyBrand(publicBranding.brand);
    } catch {
      /* a tela de acesso usa os valores padrão */
    }
    try {
      const identity = await get<Me>('/api/auth/me');
      setCsrfToken(identity.csrfToken);
      setMe(identity);
      await loadStore();
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) {
        // Falha de rede não deve parecer sessão expirada.
        console.error(err);
      }
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, [loadStore]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const login = useCallback(
    async (companySlug: string, email: string, password: string, lembrar = false) => {
      const result = await post<{ csrfToken: string; permissions: string[] }>('/api/auth/login', {
        companySlug,
        email,
        password,
        lembrar,
      });
      setCsrfToken(result.csrfToken);
      const identity = await get<Me>('/api/auth/me');
      setMe(identity);
      await loadStore();
    },
    [loadStore],
  );

  const logout = useCallback(async () => {
    await post('/api/auth/logout', {});
    setCsrfToken(null);
    setMe(null);
    setStore(null);
  }, []);

  const can = useCallback(
    (permission: string) => Boolean(me?.permissions.includes(permission)),
    [me],
  );

  const value = useMemo<SessionValue>(
    () => ({ me, branding, store, loading, can, login, logout, reloadStore: loadStore }),
    [me, branding, store, loading, can, login, logout, loadStore],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(Context);
  if (!value) throw new Error('useSession fora do SessionProvider');
  return value;
}
